import { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { string, object } from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";

import Autocomplete from "../../../../../components/react-form/Autocomplete";
import CnpjTextField from "../../../../../components/react-form/CnpjTextField";
import ZipTextField from "../../../../../components/react-form/ZipTextField";
import {
  getCities,
  getIntegrationAgent,
  getState,
  storeIntegrationAgent,
  updateIntegrationAgent,
} from "../../../../../services/pwsApi";
import isValidCnpj from "../../../../../utils/isValidCnjpj";

const integrationAgentSchema = object().shape({
  name: string().required("Informe o nome"),
  cnpj: string()
    .required("Informe o CNPJ")
    .test("is-cnpj", "O valor não corresponde a um CNPJ válido", value =>
      isValidCnpj(value),
    ),
  address: string().required("Informe o endereço"),
  addressNumber: string(),
  addressNeighborhood: string().required("Informe o bairro"),
  postalCode: string().required("Informe o CEP"),
  addressState: object()
    .nullable()
    .required("Informe a UF"),
  addressCity: object()
    .nullable()
    .required("Informe o município"),
});

export default function IntegrationAgentDialog({ onClose, agentId }) {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();

  const companyCode = searchParams.get("company_code");
  const company = companyCode?.slice(0, 3);
  const subsidiary = companyCode?.slice(3, 7);

  const integrationAgentQuery = useQuery(
    ["integration-agent", agentId],
    () => getIntegrationAgent(company, subsidiary, agentId),
    {
      enabled: !!agentId && !!company && !!subsidiary,
      select: response => {
        const agent = response.data?.data;
        if (!agent) return null;
        return {
          id: agent.R_E_C_N_O_,
          name: agent.RFR_NOME,
          cnpj: agent.RFR_CNPJ,
          address: agent.RFR_END,
          addressNumber: agent.RFR_NREND,
          addressComplement: agent.RFR_COMPL,
          addressNeighborhood: agent.RFR_BAIRRO,
          postalCode: agent.RFR_CEP,
          addressState: agent.RFR_UF
            ? { value: agent.RFR_UF, label: agent.RFR_UF }
            : null,
          addressCity: agent.RFR_CODMUN
            ? { value: agent.RFR_CODMUN, label: agent.CC2_MUN }
            : null,
        };
      },
    },
  );

  const data = integrationAgentQuery.data;

  const statesQuery = useQuery(
    ["states", company, subsidiary],
    () => getState(company, subsidiary),
    {
      enabled: !!company && !!subsidiary,
      select: response =>
        response.data.data.map(st => ({
          value: st?.X5_CHAVE,
          label: st?.X5_CHAVE,
        })),
      onError: () => toast.error("Erro ao carregar estados."),
    },
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(integrationAgentSchema),
    defaultValues: {
      id: data?.id ?? null,
      name: data?.name ?? "",
      cnpj: data?.cnpj ?? "",
      address: data?.address ?? "",
      addressNumber: data?.addressNumber ?? "",
      addressComplement: data?.addressComplement ?? "",
      addressNeighborhood: data?.addressNeighborhood ?? "",
      postalCode: data?.postalCode ?? "",
      addressState: data?.addressState ?? null,
      addressCity: data?.addressCity ?? null,
    },
  });

  const addressState = watch("addressState");

  const citiesQuery = useQuery(
    ["cities", company, subsidiary, addressState?.value],
    () => getCities(company, subsidiary, addressState?.value),
    {
      enabled: !!company && !!subsidiary && !!addressState?.value,
      select: response =>
        response.data.data.map(city => ({
          value: city?.CC2_CODMUN,
          label: city?.CC2_MUN,
        })),
      onError: () => toast.error("Erro ao carregar cidades."),
    },
  );

  useEffect(() => {
    if (data) {
      reset({
        id: data.id ?? null,
        name: data.name ?? "",
        cnpj: data.cnpj ?? "",
        address: data.address ?? "",
        addressNumber: data.addressNumber ?? "",
        addressComplement: data.addressComplement ?? "",
        addressNeighborhood: data.addressNeighborhood ?? "",
        postalCode: data.postalCode ?? "",
        addressState: data.addressState ?? "",
        addressCity: data.addressCity ?? "",
      });
    }
  }, [data, reset]);

  const storeMutation = useMutation(storeIntegrationAgent, {
    onSuccess: () => {
      toast.success("Agente de integração criado com sucesso.");
      queryClient.invalidateQueries("integration-agents");
      queryClient.invalidateQueries("integration-agent");
      onClose();
    },
    onError: () => {
      toast.error("Erro ao criar agente de integração.");
    },
  });

  const updateMutation = useMutation(
    variables => updateIntegrationAgent(variables.id, variables),
    {
      onSuccess: () => {
        toast.success("Agente de integração atualizado com sucesso.");
        queryClient.invalidateQueries("integration-agents");
        queryClient.invalidateQueries("integration-agent");
        onClose();
      },
      onError: () => {
        toast.error("Erro ao atualizar agente de integração.");
      },
    },
  );

  const onSubmit = formData => {
    const {
      id,
      name,
      cnpj,
      address,
      addressNumber,
      addressComplement,
      addressNeighborhood,
      postalCode,
      addressState,
      addressCity,
    } = formData;

    const payload = {
      companyCode: company,
      subsidiaryCode: subsidiary,
      name,
      cnpj: cnpj?.replace(/\D/g, ""),
      address,
      addressNumber,
      addressComplement,
      neighborhood: addressNeighborhood,
      zipCode: postalCode?.replace(/\D/g, ""),
      state: addressState?.value,
      cityCode: addressCity?.value,
    };

    if (id) {
      updateMutation.mutate({ id, ...payload });
    } else {
      storeMutation.mutate(payload);
    }
  };

  return (
    <Dialog maxWidth="md" fullWidth open onClose={onClose}>
      <DialogTitle>
        {!data?.id
          ? "Adicionar agente de integração"
          : "Editar agente de integração"}
      </DialogTitle>

      <DialogContent dividers>
        <div className="row">
          <div className="col-md-6">
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label="Nome"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              name="cnpj"
              control={control}
              render={({ field }) => (
                <CnpjTextField
                  {...field}
                  required
                  label="CNPJ"
                  margin="normal"
                  fullWidth
                  error={!!errors.cnpj}
                  helperText={errors.cnpj?.message}
                />
              )}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-3">
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => (
                <ZipTextField
                  {...field}
                  required
                  label="CEP"
                  margin="normal"
                  fullWidth
                  error={!!errors.postalCode}
                  helperText={errors.postalCode?.message}
                />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label="Endereço"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.address}
                  helperText={errors.address?.message}
                />
              )}
            />
          </div>
          <div className="col-md-3">
            <Controller
              name="addressNumber"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Número"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.addressNumber}
                  helperText={errors.addressNumber?.message}
                />
              )}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <Controller
              name="addressComplement"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Complemento"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.addressComplement}
                  helperText={errors.addressComplement?.message}
                />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              name="addressNeighborhood"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  required
                  label="Bairro"
                  margin="normal"
                  fullWidth
                  variant="standard"
                  error={!!errors.addressNeighborhood}
                  helperText={errors.addressNeighborhood?.message}
                />
              )}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <Controller
              name="addressState"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Autocomplete
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  options={statesQuery.data || []}
                  loading={statesQuery.isLoading}
                  inputprops={{
                    label: "UF",
                    required: true,
                    fullWidth: true,
                    error: !!errors?.addressState,
                    helperText: errors?.addressState?.message,
                    margin: "normal",
                  }}
                />
              )}
            />
          </div>
          <div className="col-md-6">
            <Controller
              name="addressCity"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Autocomplete
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  options={citiesQuery.data || []}
                  loading={citiesQuery.isLoading}
                  disabled={!addressState?.value}
                  inputprops={{
                    label: "Município",
                    required: true,
                    fullWidth: true,
                    error: !!errors?.addressCity,
                    helperText: errors?.addressCity?.message,
                    margin: "normal",
                  }}
                />
              )}
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          color="primary"
        >
          {!data?.id ? "Adicionar" : "Editar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
